import { clone } from 'ramda';
import { FeatureFlagController } from '../interface/feature-flag-controller';
import type {
    FeatureFlagKey,
    FeatureFlag,
    FeatureFlagIdentifyInfo,
    FeatureFlagTransform,
    FeatureFlagSource,
    FeatureFlagSourceName,
    FeatureFlagsMap,
} from '../types';
import { FeatureFlagEvent } from '../types';
import { FEATURE_FLAGS } from '../../constants/feature-flags';
import { EventEmitter } from '../../event-emitter';
import { getPrefixedLogger, prefixes } from '../../rwc-logger';

const logger = getPrefixedLogger(prefixes.VC_UTILS, 'feature-flags-client');

/**
 * Manage the flags obtained from the backend, the data source may be ffs, or other data sources.
 * All feature flag data sources will implement the FeatureFlagController interface and have the same API.
 * When multiple data sources are injected, the final flag value is obtained according to the priority and whether the current data source supports this flag.
 */
class FeatureFlagService extends EventEmitter {
    private _sources: FeatureFlagSource[] = [];
    private _isInitialized = false;
    private _flagVersion = 1;
    private _frozenAllFlags: FeatureFlagsMap | null = null;
    private _frozenFlagVersion: number | null = null;

    constructor() {
        super();
    }
    /**
     * determine which data source the current flag should take data from according to the key (ffs now)
     * @param type
     */
    private _getSourceWithHighestPriority(type: FeatureFlagKey) {
        return this._sources.find(item => {
            return Boolean(item.controller.isReady && item.controller.isFlagSupported(type));
        });
    }
    /**
     * When all data sources are initialized, trigger the INITIALIZED event
     * Initialized include (success and failed),as long as it is not in pending state
     */
    private _checkRemoteFeatureFlagStatus = (): void => {
        if (!this._isInitialized) {
            if (!this._sources.find(item => item.controller.isPending)) {
                this._isInitialized = true;
                this.emit(FeatureFlagEvent.INITIALIZED);
            }
        }
    };
    /**
     * inject data source
     * @param controller
     * @param priority
     */
    public addSource(
        controller: FeatureFlagController,
        priority: number,
        transform: FeatureFlagTransform
    ): void {
        this._sources.push({ priority, controller, transform });
        this._sources.sort((a, b) => b.priority - a.priority);
        controller.on(FeatureFlagEvent.SUCCEED, this._onSourceChanged);
        controller.on(FeatureFlagEvent.SUCCEED, this._checkRemoteFeatureFlagStatus);
        controller.on(FeatureFlagEvent.FAILED, this._checkRemoteFeatureFlagStatus);
        controller.on(FeatureFlagEvent.CHANGED, this._onSourceChanged);
    }

    private _onSourceChanged = () => {
        this._flagVersion += 1;

        if (this._frozenAllFlags) return;

        this.emit(FeatureFlagEvent.CHANGED);
    };

    public isSourceSupported(sourceName: FeatureFlagSourceName) {
        return !!this._sources.find(item => item.controller.sourceName === sourceName);
    }

    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    public get isEnabled(): boolean {
        return Boolean(this._sources.length);
    }

    public freeze(): void {
        if (this._frozenAllFlags) return;

        logger.info('freeze flags');
        this._frozenFlagVersion = this._flagVersion;
        this._frozenAllFlags = clone(this._getAllFlagsFromSources());
    }

    public unfreeze(): void {
        if (!this._frozenAllFlags) return;

        logger.info('unfreeze flags');
        const frozenFlagVersion = this._frozenFlagVersion;
        this._frozenAllFlags = null;
        this._frozenFlagVersion = null;

        if (frozenFlagVersion !== this._flagVersion) {
            this.emit(FeatureFlagEvent.CHANGED);
        }
    }

    private _getAllFlagsFromSources(): FeatureFlagsMap {
        const keys = Object.values(FEATURE_FLAGS);
        const flags: FeatureFlagsMap = {};
        keys.forEach((key: FeatureFlagKey) => {
            const source = this._getSourceWithHighestPriority(key);
            if (source) {
                const { controller, transform } = source;
                const flag = controller.getFeatureFlag(key);
                flags[key] = {
                    originKey: controller.getFeatureFlagOriginKey(key),
                    featureFlag: transform ? transform(flag) : flag,
                    source: controller.sourceName,
                };
            }
        });
        return flags;
    }

    /**
     * get all flags after judge the source
     */
    public getAllFlags(): FeatureFlagsMap {
        return this._frozenAllFlags ? clone(this._frozenAllFlags) : this._getAllFlagsFromSources();
    }

    public getFlagVersion() {
        return this._flagVersion;
    }
    /**
     * is a quick way to get the boolean feature flag
     * get whether feature flag enabled by key after judge the source
     * @param type
     */
    public getFeatureFlagEnabled(type: FeatureFlagKey) {
        return this.getFeatureFlag(type) === true;
    }
    /**
     * get origin feature flag value by key after judge the source
     * @param type
     */
    public getFeatureFlag(type: FeatureFlagKey): FeatureFlag {
        return this._frozenAllFlags
            ? this._getFrozenFeatureFlag(type)
            : this._getFeatureFlagFromSources(type);
    }

    private _getFrozenFeatureFlag(type: FeatureFlagKey): FeatureFlag {
        const featureFlag = this._frozenAllFlags?.[type]?.featureFlag;
        return featureFlag === undefined ? null : featureFlag;
    }

    private _getFeatureFlagFromSources(type: FeatureFlagKey): FeatureFlag {
        const source = this._getSourceWithHighestPriority(type);
        if (source) {
            const { controller, transform } = source;
            const flag = controller.getFeatureFlag(type);
            return transform ? transform(flag) : flag;
        }
        return null;
    }

    /**
     * identify the user and refresh the flags
     * @param identifyInfo
     * @param callback
     */
    public identify(identifyInfo: FeatureFlagIdentifyInfo) {
        this._sources.forEach(item => {
            item.controller.isReady && item.controller.identify(identifyInfo);
        });
    }
}
export default FeatureFlagService;
